import * as THREE from 'three';
import { C } from '../core/palette.js';
import { box, cyl, sphere, textSprite } from '../core/meshkit.js';
import { BreadBoi } from '../entities/breadBoi.js';
import { Gary } from '../entities/gary.js';
import { HUD } from '../ui/hud.js';
import { addCrumbs } from '../core/save.js';
import { ComicSort } from '../minigames/comicSort.js';

// Gary's Comics — the comic shop. Bread Boi browses the aisles, then steps up
// to Gary's counter to help sort the new arrivals (the sorting minigame).
// States: 'explore' → 'playing' → 'results'.

export function createComicsScene(){
  return () => new ComicsScene();
}

const SPINE = [C.raspberry, C.cobalt, C.tangerine, C.sprout, C.toast, C.breeze];

class ComicsScene {
  constructor(){
    this.state = 'explore';
    this.sorter = null;
    this.panels = [];

    this.scene = new THREE.Scene();
    this.scene.background = new THREE.Color(0x2A2140);        // cozy dim shop

    this.camera = new THREE.PerspectiveCamera(52, window.innerWidth/window.innerHeight, 0.1, 100);
    this.camDist = 10;
    this.camTarget = new THREE.Vector3(0, 1.6, 0);
    this.azimuth = 0; this.polar = 0.48;

    this._buildRoom();
    this._buildShelves();
    this._buildCounter();

    this.gary = new Gary();
    this.gary.root.position.set(0, 0.4, -1.9);               // behind the counter
    this.scene.add(this.gary.root);

    this.bread = new BreadBoi();
    this.bread.root.position.set(0, 0, 4);
    this.bread.root.rotation.y = Math.PI;
    this.scene.add(this.bread.root);

    this.hotspots = [
      { pos: new THREE.Vector3(0, 0, 0.6), r: 2.2, label: "📚 sort comics for Gary", action: () => this._startSort() },
      { pos: new THREE.Vector3(0, 0, 5.2), r: 2.2, label: '← back to town', action: () => this.manager.goTo('town', { from: 'comics' }) }
    ];
    this.nearSpot = null;
  }

  _buildRoom(){
    this.scene.add(new THREE.AmbientLight(0xB0A8D0, 0.7));
    const key = new THREE.DirectionalLight(0xFFF2D8, 0.6);
    key.position.set(3, 9, 6); key.castShadow = true; key.shadow.mapSize.set(1024, 1024);
    this.scene.add(key);
    const warm = new THREE.PointLight(0xF49B33, 0.5, 12);
    warm.position.set(0, 3, 2);
    this.scene.add(warm);

    const floor = new THREE.Mesh(new THREE.PlaneGeometry(14, 14), new THREE.MeshLambertMaterial({ color: C.rye }));
    floor.rotation.x = -Math.PI/2; floor.receiveShadow = true; this.scene.add(floor);
    const back = new THREE.Mesh(new THREE.PlaneGeometry(14, 8), new THREE.MeshLambertMaterial({ color: 0x3D2E63 }));
    back.position.set(0, 4, -6); back.receiveShadow = true; this.scene.add(back);
    const sideL = back.clone(); sideL.rotation.y = Math.PI/2; sideL.position.set(-7, 4, 0); this.scene.add(sideL);
    const sideR = back.clone(); sideR.rotation.y = -Math.PI/2; sideR.position.set(7, 4, 0); this.scene.add(sideR);

    const marquee = textSprite("GARY'S COMICS", 30, '#FFF4DF', 4.4, 1.1);
    marquee.position.set(0, 3.4, -5.7); this.scene.add(marquee);

    this.exitDoor = box(1.5, 2.4, 0.3, C.crustDark, 0, 1.2, 5.9, this.scene);
    const sign = textSprite('← exit to town', 24, '#FFF4DF', 2.8, 0.8);
    sign.position.set(0, 2.9, 5.7); this.scene.add(sign);
  }

  _buildShelves(){
    // a bookshelf full of randomly-colored comic spines
    const shelf = (x, z, rotY) => {
      const g = new THREE.Group(); g.position.set(x, 0, z); g.rotation.y = rotY; this.scene.add(g);
      box(2.6, 3.0, 0.5, C.crustDark, 0, 1.5, 0, g);              // cabinet
      for (let row = 0; row < 3; row++){
        const y = 0.7 + row * 0.9;
        for (let i = 0; i < 10; i++){
          const sx = -1.1 + i * 0.24;
          const h = 0.6 + Math.random() * 0.14;
          box(0.18, h, 0.3, SPINE[(Math.random() * SPINE.length) | 0], sx, y + h/2 - 0.35, 0.16, g);
        }
      }
    };
    shelf(-4.5, -5.4, 0); shelf(-1.5, -5.4, 0); shelf(1.5, -5.4, 0); shelf(4.5, -5.4, 0);
    shelf(-6.4, -1.5, Math.PI/2); shelf(-6.4, 1.5, Math.PI/2);
    shelf(6.4, -1.5, -Math.PI/2); shelf(6.4, 1.5, -Math.PI/2);

    // a spinner rack of comics in the middle-left
    const rack = new THREE.Group(); rack.position.set(-3.6, 0, 1.5); this.scene.add(rack);
    cyl(0.08, 0.08, 1.8, C.crustDark, 0, 0.9, 0, rack, 8);
    for (let i = 0; i < 6; i++){
      const a = i / 6 * Math.PI * 2;
      box(0.5, 0.7, 0.06, SPINE[i % SPINE.length], Math.cos(a) * 0.45, 1.4, Math.sin(a) * 0.45, rack).rotation.y = -a;
    }
  }

  _buildCounter(){
    const g = new THREE.Group(); g.position.set(0, 0, -1); this.scene.add(g);
    box(4.0, 1.1, 1.0, C.crust, 0, 0.55, 0, g);                 // counter body
    box(4.2, 0.12, 1.2, C.crustDark, 0, 1.15, 0, g);           // countertop
    // a stack of unsorted comics + a register on the counter
    box(0.6, 0.5, 0.5, C.raspberry, -1.2, 1.45, 0, g);
    box(0.55, 0.1, 0.45, C.tangerine, -1.2, 1.72, 0, g);
    box(0.5, 0.4, 0.4, 0x333333, 1.3, 1.4, 0, g);              // register
    // sorting bins hint behind counter
    [C.raspberry, C.cobalt, C.sprout, C.tangerine].forEach((col, i) =>
      box(0.5, 0.5, 0.5, col, -1.2 + i * 0.8, 0.25, -1.2, g));
  }

  // ---- lifecycle ----
  enter({ manager }){
    this.manager = manager;
    this.input = manager.input;
    this.input.useCamera(this.camera);
    this.input.polarRange = [0.28, 0.62];
    this._toExplore(true);
  }

  exit(){
    this._teardownGame();
    this._clearPanels();
    this.input.onTap = null;
    HUD.showEnter(null); HUD.say('');
  }

  _toExplore(first){
    this.state = 'explore';
    this._clearPanels();
    this.bread.resetLimbs();
    this.input.onTap = (ndc, ray) => this._exploreTap(ray);
    HUD.setSubtitle("gary's comics");
    if (first) HUD.say("Gary's Comics! Browse the aisles, then step up to the counter to help Gary sort the new arrivals.");
    HUD.setHint('click to walk · walk up to the counter or the exit');
    HUD.hideHintSoon(7000);
  }

  _exploreTap(ray){
    const dir = ray.ray.direction;
    if (Math.abs(dir.y) < 1e-4) return;
    const t = -ray.ray.origin.y / dir.y;
    if (t <= 0) return;
    const p = ray.ray.origin.clone().addScaledVector(dir, t);
    const gp = new THREE.Vector3(p.x, 0, p.z);
    gp.x = Math.max(-6, Math.min(6, gp.x));
    gp.z = Math.max(0.3, Math.min(5.4, gp.z));       // stay on the customer side of the counter
    HUD.hideHint();
    this.bread.walkTo(gp);
  }

  // ---- panels (shared .cafe-* game UI) ----
  _panel(html, bare){
    const el = document.createElement('div');
    el.className = bare ? '' : 'cafe-panel';
    el.innerHTML = html;
    document.body.appendChild(el);
    this.panels.push(el);
    return el;
  }
  _clearPanels(){ this.panels.forEach(p => p.remove()); this.panels = []; }

  _startSort(){
    this.state = 'playing';
    this._clearPanels();
    this.input.onTap = null;
    HUD.showEnter(null); HUD.setHint(''); HUD.hideHint(); HUD.say('');

    this.bread.stop();
    this.bread.root.position.set(0, 0, 1.4);
    this.bread.root.rotation.y = Math.PI;

    this.sorter = new ComicSort({
      onSort: (r) => { if (r.right) this.gary.nod(); },
      onFinish: (r) => this._results(r)
    });
    this.sorter.mount();
    this.sorter.begin();

    const q = this._panel('<button class="cafe-quit">✕ leave</button>', 'bare');
    q.querySelector('.cafe-quit').onclick = () => { this._teardownGame(); this._toExplore(false); };
  }

  _results(r){
    this.state = 'results';
    this._teardownGame();
    this.bread.drawFace('grin');
    if (r.crumbs > 0) addCrumbs(r.crumbs);
    HUD.refreshCrumbs();

    const grade = r.accuracy >= 95 ? 'S' : r.accuracy >= 85 ? 'A' : r.accuracy >= 70 ? 'B' : r.accuracy >= 50 ? 'C' : 'D';
    const p = this._panel(`
      <div class="cafe-card">
        <div class="cafe-grade">${grade}</div>
        <h2>Shift done!</h2>
        <div class="cafe-stats">
          <div><b>${r.correct}</b><span>sorted</span></div>
          <div><b>${r.wrong}</b><span>wrong</span></div>
          <div><b>${r.bestCombo}</b><span>best combo</span></div>
          <div><b>${r.accuracy}%</b><span>accuracy</span></div>
        </div>
        <p class="cafe-payout">🍞 +${r.crumbs} crumbs</p>
        <button class="cafe-btn again">↻ sort again</button>
        <button class="cafe-btn ghost leave">← back to the shop</button>
      </div>`);
    p.querySelector('.again').onclick = () => this._startSort();
    p.querySelector('.leave').onclick = () => this._toExplore(false);
  }

  _teardownGame(){
    if (this.sorter){ this.sorter.unmount(); this.sorter = null; }
  }

  update(dt){
    this.gary.update(dt);

    if (this.state === 'playing'){
      this.sorter.update(dt);
      this.camera.position.set(2.6, 2.7, 3.4);            // look toward Gary at the counter
      this.camera.lookAt(0, 1.3, -1.4);
      return;
    }

    this.bread.update(dt);
    if (this.state === 'explore'){
      let near = null, nd = Infinity;
      this.hotspots.forEach(h => { const d = h.pos.distanceTo(this.bread.root.position); if (d < nd){ nd = d; near = h; } });
      if (near && nd < near.r){
        if (this.nearSpot !== near){ this.nearSpot = near; HUD.showEnter(near.label, () => near.action()); }
      } else if (this.nearSpot){ this.nearSpot = null; HUD.showEnter(null); }
    }

    this.azimuth += (this.input.azimuthGoal - this.azimuth) * 0.08;
    this.polar   += (this.input.polarGoal   - this.polar)   * 0.08;
    this.camTarget.lerp(new THREE.Vector3(this.bread.root.position.x * 0.4, 1.6, this.bread.root.position.z * 0.4), 0.06);
    this.camera.position.set(
      this.camTarget.x + this.camDist * Math.sin(this.azimuth) * Math.cos(this.polar),
      this.camTarget.y + this.camDist * Math.sin(this.polar),
      this.camTarget.z + this.camDist * Math.cos(this.azimuth) * Math.cos(this.polar)
    );
    this.camera.lookAt(this.camTarget);
  }

  onResize(){
    this.camera.aspect = window.innerWidth / window.innerHeight;
    this.camera.updateProjectionMatrix();
  }
}
